import { mask as applyMask, unmask as removeMask } from "remask";

import { numberToBrl } from "./currency.utils";
import { MASK_PATTERN } from "../types/masks";

const plainCurrencyValueRegex = /^\d+(\.\d+)?$/;

export type MaskType = keyof typeof MASK_PATTERN;

/** Maximum digit count per mask type (used to cap input on mobile). */
export const MASK_MAX_DIGITS: Partial<Record<MaskType, number>> = {
  cep: 8,
  cpf: 11,
  cnpj: 14,
  phone: 10,
  cellphone: 11,
  date: 8,
  card: 16,
  cardDate: 6,
  number: 7
};

export const stripDigits = (value?: string | null): string =>
  (value ?? "").replace(/\D/g, "");

export const capDigitsForMask = (
  digits: string,
  maskType: MaskType
): string => {
  const maxDigits = MASK_MAX_DIGITS[maskType];
  return maxDigits ? digits.slice(0, maxDigits) : digits;
};

// Get mask pattern by type
const getMaskPattern = (maskType: MaskType) => {
  return MASK_PATTERN[maskType];
};

/**
 * Applies the CPF mask as the user types:
 * 581 → 581
 * 5810 → 581.0
 * 5810148 → 581.014.8
 * 58101489304 → 581.014.893-04
 */
export const applyCpfMaskProgressive = (value: string): string => {
  const digits = stripDigits(value).slice(0, MASK_MAX_DIGITS.cpf);

  if (!digits) {
    return "";
  }

  const part1 = digits.slice(0, 3);
  const part2 = digits.slice(3, 6);
  const part3 = digits.slice(6, 9);
  const part4 = digits.slice(9, 11);

  if (digits.length <= 3) {
    return part1;
  }

  if (digits.length <= 6) {
    return `${part1}.${part2}`;
  }

  if (digits.length <= 9) {
    return `${part1}.${part2}.${part3}`;
  }

  return `${part1}.${part2}.${part3}-${part4}`;
};

// Apply mask to a value
export const applyMaskToValue = (value: string, maskType: MaskType) => {
  if (maskType === "cpf") {
    return applyCpfMaskProgressive(value);
  }

  return applyMask(value, getMaskPattern(maskType));
};

/**
 * Applies phone mask automatically based on number length
 * - 11 digits: cellphone mask (xx) xxxxx-xxxx
 * - 10 digits: phone mask (xx) xxxx-xxxx
 * @param value - Phone number string
 * @returns Formatted phone string
 */
export const applyPhoneMask = (value: string): string => {
  if (!value) return "";
  const cleaned = value.replace(/\D/g, "");
  const length = cleaned.length;

  if (length === 11) {
    return applyMaskToValue(cleaned, "cellphone");
  } else if (length === 10) {
    return applyMaskToValue(cleaned, "phone");
  }
  return value;
};

// Get masked and unmasked values
export const getMaskedAndUnmaskedValues = (value: string, mask: MaskType) => {
  const maskedValue = applyMaskToValue(value, mask);
  const unmaskedValue = removeMask(maskedValue);

  return { masked: maskedValue, unmasked: unmaskedValue };
};

/** Formats currency from a raw digit string (cent accumulation while typing). */
export const applyMaskToCurrencyFromDigits = (digits: string) => {
  const numberValue = Number(stripDigits(digits) || "0") / 100;
  return numberToBrl(numberValue);
};

/** Formats currency from a numeric amount in reais (display/API sync). */
export const applyMaskToCurrencyValue = (value: string) => {
  const numericValue = Number(value);
  return numberToBrl(Number.isNaN(numericValue) ? 0 : numericValue);
};

export const removeMaskToCurrencyValue = (value: string) => {
  if (!value) return 0;

  if (plainCurrencyValueRegex.test(value)) {
    return Number(value);
  }

  const normalized = value
    .replace(/[^\d,.-]/g, "")
    .replace(/\./g, "")
    .replace(",", ".");

  const parsed = parseFloat(normalized);
  return Number.isNaN(parsed) ? 0 : parsed;
};

/**
 * Formats CPF string to display format (xxx.xxx.xxx-xx)
 * @param cpf - CPF string with or without mask
 * @returns Formatted CPF string
 */
export const formatCPF = (cpf: string): string => {
  if (!cpf) return "";
  return applyMaskToValue(cpf, "cpf");
};

/**
 * Formats phone number to display format using applyPhoneMask
 * - Cellphone (11 digits): (xx) xxxxx-xxxx
 * - Phone (10 digits): (xx) xxxx-xxxx
 * @param ddd - Area code (DDD)
 * @param phone - Phone number
 * @returns Formatted phone string
 */
export const formatCellPhone = (ddd?: string, phone?: string): string => {
  if (!ddd || !phone) return "";
  const fullPhone = `${ddd}${phone}`;
  return applyPhoneMask(fullPhone);
};
